import type { UIMessage } from "@ai-sdk/react";
import { useQuery } from "urql";
import type { ResultOf } from "@/data-access/graphql";
import { GetConversationMessages } from "@/data-access/herald-chat/chat-queries";
import useHeraldChatHistoryStore from "@/store/herald-chat/herald-chat-history.slice";

/** Matches GraphQL `conversationMessages` pagination limit used by Herald chat loaders. */
export const CONVERSATION_MESSAGES_PAGE_SIZE = 100;

type MessageResult = NonNullable<
  ResultOf<typeof GetConversationMessages>["conversationMessages"]["results"]
>[number];

/** Metadata attached to Herald chat messages restored from conversation history. */
export interface HeraldChatMessageMetadata {
  createdAt?: string;
}

export const mapConversationMessageResultsToUiMessages = (
  raw: MessageResult[],
): UIMessage[] =>
  raw.flatMap((msg) => {
    const role = msg.role.toLowerCase();
    if (role !== "user" && role !== "assistant") {
      return [];
    }
    const ui: UIMessage = {
      id: msg.id,
      role,
      parts: (msg.parts as UIMessage["parts"]) ?? [
        { type: "text" as const, text: msg.content },
      ],
      metadata: {
        createdAt: msg.createdAt,
      } satisfies HeraldChatMessageMetadata,
    };
    return [ui];
  });

/**
 * Adopts the server snapshot only when it has strictly grown, since streaming IDs and
 * persisted DB IDs don't overlap and a length-equal/smaller snapshot may be mid-stream.
 */
export const mergeUiMessagesWithServerSnapshot = (
  prev: UIMessage[],
  server: UIMessage[],
): UIMessage[] => {
  if (server.length === 0) return prev;
  if (server.length <= prev.length) return prev;
  return server;
};

/** Removes only the trailing assistant row so a resumed full replay rebuilds that turn from zero. */
export const resetActiveAssistantMessage = (
  messages: UIMessage[],
  assistantMessageId: string | null,
): UIMessage[] =>
  assistantMessageId != null &&
  messages.at(-1)?.role === "assistant" &&
  messages.at(-1)?.id === assistantMessageId
    ? messages.slice(0, -1)
    : messages;

/** A 204 resume response makes the refreshed server history authoritative, including equal row counts. */
export const reconcileInactiveResumeWithServerSnapshot = (
  currentMessages: UIMessage[],
  serverMessages: UIMessage[],
): UIMessage[] =>
  serverMessages.length > 0 ? serverMessages : currentMessages;

interface UseConversationLoaderReturn {
  messages: UIMessage[] | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Loads messages for an existing conversation from the backend via GraphQL.
 * Maps backend Message objects to AI SDK UIMessage format for useChat hydration.
 * Returns null messages when no conversationId is provided (new chat).
 */
const useRetrievePreviousConversation = (
  conversationId: string | undefined,
): UseConversationLoaderReturn => {
  const skipMessagesFetchForConversationId = useHeraldChatHistoryStore(
    (state) => state.skipMessagesFetchForConversationId,
  );

  const skipFetch =
    conversationId !== undefined &&
    skipMessagesFetchForConversationId === conversationId;

  const [{ data, fetching, error }] = useQuery({
    query: GetConversationMessages,
    variables: {
      filters: { conversationId: conversationId ?? "" },
      pagination: { limit: CONVERSATION_MESSAGES_PAGE_SIZE },
    },
    pause: !conversationId || skipFetch,
  });

  if (!conversationId) {
    return { messages: null, isLoading: false, error: null };
  }

  /**
   * First-stream registration pauses this query; urql may still surface `data` from an
   * earlier conversation. Feeding that into `useChat` remount logic shows the wrong thread.
   */
  if (skipFetch) {
    return { messages: null, isLoading: false, error: null };
  }

  const rawMessages = data?.conversationMessages?.results;

  const messages: UIMessage[] | null = rawMessages
    ? mapConversationMessageResultsToUiMessages(rawMessages)
    : null;

  return {
    messages,
    isLoading: fetching,
    error: error?.message ?? null,
  };
};

export default useRetrievePreviousConversation;
